<template>
  <div>
    <v-container fluid>
      <!-- Header Toolbar -->
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar :image="SubNavbarBackGround" color="teal" class="header-title text-white rounded-lg">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Tool Management</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <!-- Main Card -->
      <v-card class="main-card" style="margin-top: 1rem">
        <v-card-title class="py-4 px-4">
          <v-row align="center" no-gutters>
            <v-col cols="12" sm="12" md="4" class="d-flex align-center pb-3 pb-md-0">
              <v-icon color="teal-darken-1" size="28" class="mr-3">mdi-robot-industrial</v-icon>
              <div>
                <h3 class="text-h6 mb-1">Agent Tool Configuration</h3>
                <p class="text-caption text-grey-darken-1 mb-0">Manage External API Tools for Flow Engine</p>
              </div>
            </v-col>
            <v-col cols="12" sm="12" md="8">
              <v-row align="center" justify="end" no-gutters>
                <v-col cols="12" sm="5" md="5">
                  <v-text-field
                    v-model="searchText"
                    density="compact"
                    variant="outlined"
                    placeholder="Search Tool..."
                    prepend-inner-icon="mdi-magnify"
                    hide-details
                    clearable
                    class="search-field"
                    @update:model-value="onSearchDebounced"
                  />
                </v-col>
                <v-col v-if="canAdd" cols="auto" class="ml-2 ml-sm-3">
                  <v-btn color="teal" class="text-none add-btn" prepend-icon="mdi-plus" @click="openAddDialog">
                    Add Tool
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-item>
          <div class="table-container">
            <v-data-table-server
              v-model:options="options"
              :headers="headers"
              :items="items"
              :items-length="totalItems"
              :loading="loading"
              item-key="id"
              class="elevation-0 my-data-table modern-table"
              density="comfortable"
              :items-per-page="options.itemsPerPage"
              :items-per-page-options="[10, 25, 50, 100]"
              fixed-header
            >
              <template #item.name="{ item }">
                <div class="d-flex align-center">
                  <div>
                    <span class="font-weight-medium">{{ item.name }}</span>
                    <div v-if="item.description" class="text-caption text-grey text-truncate" style="max-width: 200px;">
                      {{ item.description }}
                    </div>
                  </div>
                </div>
              </template>

              <template #item.httpMethod="{ item }">
                <v-chip
                  :color="getMethodColor(item.httpMethod)"
                  size="small"
                  variant="tonal"
                  class="font-weight-medium"
                >
                  {{ item.httpMethod }}
                </v-chip>
              </template>

              <template #item.apiEndpoint="{ item }">
                <code class="api-endpoint">{{ item.apiEndpoint }}</code>
              </template>

              <template #item.updateDate="{ item }">
                <span class="text-grey-darken-1">{{ formatDate(item.updateDate || item.createDate) }}</span>
              </template>

              <template #item.actions="{ item }">
                <div class="action-group">
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    color="primary"
                    class="action-btn"
                    @click="openEditDialog(item)"
                  >
                    <v-icon size="18">{{ canEdit ? 'mdi-pencil-outline' : 'mdi-eye-outline' }}</v-icon>
                    <v-tooltip activator="parent" location="top">{{ canEdit ? 'Edit' : 'View' }}</v-tooltip>
                  </v-btn>
                  <v-btn
                    v-if="canDelete"
                    icon
                    variant="text"
                    size="small"
                    color="error"
                    class="action-btn"
                    @click="openDeleteDialog(item)"
                  >
                    <v-icon size="18">mdi-delete-outline</v-icon>
                    <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                  </v-btn>
                </div>
              </template>

              <template #no-data>
                <div class="text-center py-8">
                  <v-icon size="80" color="grey-lighten-1">mdi-tools</v-icon>
                  <p class="text-grey mt-3 text-body-1">No Tool found</p>
                  <v-btn v-if="canAdd" color="teal" variant="tonal" class="mt-2" @click="openAddDialog">
                    <v-icon class="mr-1">mdi-plus</v-icon>
                    Add New Tool
                  </v-btn>
                </div>
              </template>
            </v-data-table-server>
          </div>
        </v-card-item>
      </v-card>
    </v-container>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="680px" persistent>
      <v-card class="dialog-card">
        <v-card-title class="d-flex align-center pa-4">
          <v-avatar :color="isEdit ? (canEdit ? 'primary' : 'amber-darken-3') : 'teal'" size="40" class="mr-3">
            <v-icon color="white">{{ isEdit ? (canEdit ? 'mdi-pencil' : 'mdi-eye-outline') : 'mdi-plus' }}</v-icon>
          </v-avatar>
          <div>
            <span class="text-h6 font-weight-bold">{{ isEdit ? (canEdit ? 'Edit Tool' : 'View Tool') : 'Add New Tool' }}</span>
            <p class="text-caption text-grey mb-0">Configure API Endpoint for Flow Engine</p>
          </div>
          <v-spacer />
          <v-btn icon variant="text" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <v-form ref="formRef" v-model="formValid" :disabled="isEdit ? !canEdit : !canAdd">
            <!-- Basic Info Section -->
            <div class="section-title mb-3">
              <v-icon size="18" color="teal" class="mr-2">mdi-information-outline</v-icon>
              Basic Information
            </div>
            
            <v-text-field
              v-model="formData.name"
              label="Tool Name"
              placeholder="e.g. Get Weather, Send Email"
              prepend-inner-icon="mdi-tag-outline"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required]"
              hide-details="auto"
              class="mb-3"
            />

            <v-textarea
              v-model="formData.description"
              label="Description"
              placeholder="Describe how this tool works"
              prepend-inner-icon="mdi-text-box-outline"
              variant="outlined"
              density="comfortable"
              rows="2"
              auto-grow
              hide-details="auto"
              class="mb-4"
            />

            <!-- API Configuration Section -->
            <div class="section-title mb-3">
              <v-icon size="18" color="teal" class="mr-2">mdi-api</v-icon>
              API Configuration
            </div>

            <v-row>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="formData.apiEndpoint"
                  label="API Endpoint"
                  placeholder="https://api.example.com/endpoint"
                  prepend-inner-icon="mdi-link-variant"
                  variant="outlined"
                  density="comfortable"
                  :rules="[rules.required, rules.url]"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="formData.httpMethod"
                  label="HTTP Method"
                  prepend-inner-icon="mdi-swap-horizontal"
                  variant="outlined"
                  density="comfortable"
                  :items="httpMethods"
                  hide-details="auto"
                >
                  <template #item="{ item, props }">
                    <v-list-item v-bind="props">
                      <template #prepend>
                        <v-chip :color="getMethodColor(item.value)" size="x-small" variant="flat" class="mr-2">
                          {{ item.value }}
                        </v-chip>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <!-- Headers Section -->
            <div class="section-title mt-5 mb-3 d-flex align-center">
              <v-icon size="18" color="teal" class="mr-2">mdi-code-braces</v-icon>
              Headers
              <v-spacer />
              <v-btn size="small" variant="tonal" color="teal" @click="addHeader">
                <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                Add Header
              </v-btn>
            </div>

            <v-expand-transition>
              <div v-if="formData.headers.length > 0">
                <div
                  v-for="(header, index) in formData.headers"
                  :key="'header-' + index"
                  class="header-row d-flex align-center mb-2 pa-2 rounded-lg"
                >
                  <v-text-field
                    v-model="header.key"
                    label="Key"
                    placeholder="Authorization"
                    variant="outlined"
                    density="compact"
                    class="mr-2"
                    hide-details
                  />
                  <v-text-field
                    v-model="header.value"
                    label="Value"
                    placeholder="Bearer token..."
                    variant="outlined"
                    density="compact"
                    class="mr-2"
                    hide-details
                  />
                  <v-btn icon variant="text" color="error" size="small" @click="removeHeader(index)">
                    <v-icon size="18">mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
              <div v-else class="empty-section text-center py-3 rounded-lg mb-2">
                <v-icon size="24" color="grey-lighten-1">mdi-code-braces</v-icon>
                <p class="text-caption text-grey mb-0 mt-1">No Headers</p>
              </div>
            </v-expand-transition>

            <!-- Parameters Section -->
            <div class="section-title mt-4 mb-3 d-flex align-center">
              <v-icon size="18" color="teal" class="mr-2">mdi-variable</v-icon>
              Parameters
              <v-chip size="x-small" color="grey" variant="tonal" class="ml-2">{{ formData.parameters.length }}</v-chip>
              <v-spacer />
              <v-btn size="small" variant="tonal" color="teal" @click="addParameter">
                <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                Add Parameter
              </v-btn>
            </div>

            <v-expand-transition>
              <div v-if="formData.parameters.length > 0" class="params-container">
                <v-card
                  v-for="(param, index) in formData.parameters"
                  :key="'param-' + index"
                  class="param-card mb-3"
                  variant="outlined"
                >
                  <div class="param-header d-flex align-center px-3 py-2">
                    <v-icon size="16" color="teal" class="mr-2">mdi-code-braces-box</v-icon>
                    <span class="text-caption font-weight-medium">Parameter {{ index + 1 }}</span>
                    <v-spacer />
                    <v-chip
                      v-if="param.required"
                      size="x-small"
                      color="error"
                      variant="tonal"
                      class="mr-2"
                    >
                      Required
                    </v-chip>
                    <v-chip
                      size="x-small"
                      :color="getTypeColor(param.type)"
                      variant="tonal"
                    >
                      {{ param.type }}
                    </v-chip>
                    <v-btn
                      icon
                      variant="text"
                      color="error"
                      size="x-small"
                      class="ml-2"
                      @click="removeParameter(index)"
                    >
                      <v-icon size="16">mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <v-divider />
                  <div class="pa-3">
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="param.name"
                          label="Parameter Name"
                          placeholder="e.g. city, email, user_id"
                          prepend-inner-icon="mdi-identifier"
                          variant="outlined"
                          density="compact"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="6" sm="3">
                        <v-select
                          v-model="param.type"
                          label="Data Type"
                          prepend-inner-icon="mdi-code-tags"
                          variant="outlined"
                          density="compact"
                          :items="paramTypes"
                          hide-details
                        >
                          <template #item="{ item, props }">
                            <v-list-item v-bind="props">
                              <template #prepend>
                                <v-icon size="16" :color="getTypeColor(item.value)" class="mr-2">
                                  {{ getTypeIcon(item.value) }}
                                </v-icon>
                              </template>
                            </v-list-item>
                          </template>
                        </v-select>
                      </v-col>
                      <v-col cols="6" sm="3" class="d-flex align-center justify-center">
                        <v-switch
                          v-model="param.required"
                          label="Required"
                          density="compact"
                          hide-details
                          color="teal"
                          inset
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-text-field
                          v-model="param.description"
                          label="Description"
                          placeholder="Describe how to use this parameter..."
                          prepend-inner-icon="mdi-text"
                          variant="outlined"
                          density="compact"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-card>
              </div>
              <div v-else class="empty-section text-center py-4 rounded-lg">
                <v-icon size="32" color="grey-lighten-1">mdi-variable</v-icon>
                <p class="text-body-2 text-grey mb-1 mt-2">No Parameters</p>
                <p class="text-caption text-grey-lighten-1 mb-0">Click "Add Parameter" to define values to send to the API</p>
              </div>
            </v-expand-transition>

            <!-- Test API Section -->
            <div class="section-title mt-5 mb-3 d-flex align-center">
              <v-icon size="18" color="teal" class="mr-2">mdi-play-circle-outline</v-icon>
              Test API
              <v-spacer />
              <v-btn 
                size="small" 
                variant="tonal" 
                :color="testExpanded ? 'grey' : 'teal'" 
                @click="testExpanded = !testExpanded"
              >
                <v-icon size="16" class="mr-1">{{ testExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                {{ testExpanded ? 'Hide' : 'Show' }} Test Panel
              </v-btn>
            </div>

            <v-expand-transition>
              <div v-if="testExpanded" class="test-section">
                <v-card variant="outlined" class="test-card">
                  <v-card-text class="pa-3">
                    <!-- Test Parameter Values -->
                    <div v-if="formData.parameters.length > 0" class="mb-4">
                      <div class="text-subtitle-2 font-weight-medium mb-2">
                        <v-icon size="16" class="mr-1">mdi-form-textbox</v-icon>
                        Parameter Values
                      </div>
                      <v-row dense>
                        <v-col 
                          v-for="(param, index) in formData.parameters" 
                          :key="'test-param-' + index"
                          cols="12" 
                          sm="6"
                        >
                          <v-text-field
                            v-model="testParams[param.name]"
                            :label="param.name + (param.required ? ' *' : '')"
                            :placeholder="getTestPlaceholder(param)"
                            :hint="param.description"
                            variant="outlined"
                            density="compact"
                            persistent-hint
                            class="test-param-input"
                          >
                            <template #prepend-inner>
                              <v-chip size="x-small" :color="getTypeColor(param.type)" variant="flat" class="mr-1">
                                {{ param.type }}
                              </v-chip>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                    <div v-else class="text-center py-2 mb-3">
                      <span class="text-caption text-grey">No parameters to configure</span>
                    </div>

                    <!-- Test Button -->
                    <div class="d-flex justify-center mb-3">
                      <v-btn
                        color="teal"
                        variant="flat"
                        :loading="testing"
                        :disabled="!formData.apiEndpoint"
                        @click="runTest"
                        class="px-8"
                      >
                        <v-icon class="mr-2">mdi-play</v-icon>
                        Run Test
                      </v-btn>
                    </div>

                    <!-- Test Result -->
                    <div v-if="testResult !== null" class="test-result">
                      <div class="d-flex align-center mb-2">
                        <v-icon 
                          :color="testResult.success ? 'success' : 'error'" 
                          size="20" 
                          class="mr-2"
                        >
                          {{ testResult.success ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        <span class="text-subtitle-2 font-weight-medium">
                          {{ testResult.success ? 'Success' : 'Failed' }}
                        </span>
                        <v-spacer />
                        <v-chip size="x-small" :color="getStatusColor(testResult.status)" variant="flat">
                          {{ testResult.status || 'N/A' }}
                        </v-chip>
                        <v-chip size="x-small" color="grey" variant="tonal" class="ml-2">
                          {{ testResult.duration }}ms
                        </v-chip>
                      </div>

                      <!-- Response Headers (collapsible) -->
                      <v-expansion-panels variant="accordion" class="mb-2">
                        <v-expansion-panel>
                          <v-expansion-panel-title class="py-2" style="min-height: 40px;">
                            <v-icon size="16" class="mr-2">mdi-format-list-bulleted</v-icon>
                            <span class="text-caption">Response Headers</span>
                          </v-expansion-panel-title>
                          <v-expansion-panel-text>
                            <pre class="response-headers">{{ formatHeaders(testResult.headers) }}</pre>
                          </v-expansion-panel-text>
                        </v-expansion-panel>
                      </v-expansion-panels>

                      <!-- Response Body -->
                      <div class="text-caption font-weight-medium mb-1">
                        <v-icon size="14" class="mr-1">mdi-code-json</v-icon>
                        Response Body
                      </div>
                      <pre class="response-body" :class="{ 'error-response': !testResult.success }">{{ formatResponse(testResult.data) }}</pre>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-expand-transition>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" class="text-none" @click="closeDialog">{{ (isEdit && !canEdit) ? 'Close' : 'Cancel' }}</v-btn>
          <v-btn
            v-if="isEdit ? canEdit : canAdd"
            color="teal"
            class="text-none px-6"
            :loading="saving"
            :disabled="!formValid"
            @click="saveData"
          >
            {{ isEdit ? 'Save Changes' : 'Create Tool' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="420px">
      <v-card class="dialog-card">
        <v-card-title class="font-weight-bold pa-4">
          <v-icon color="error" class="mr-2">mdi-alert-circle-outline</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="px-4">
          Do you want to delete Tool <strong>"{{ selectedItem?.name }}"</strong>?
          <br />
          <span class="text-caption text-grey">This action cannot be undone</span>
        </v-card-text>
        <v-card-actions class="pb-4 px-4">
          <v-spacer />
          <v-btn variant="text" class="text-none" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" class="text-none" :loading="deleting" @click="confirmDelete">Delete Tool</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" location="bottom right">
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{ snackbarColor === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        {{ snackbarText }}
      </div>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'ToolManagement',
  inject: ['flagNav', 'FontColorTitle', 'SubNavbarBackGround'],
  computed: {
    isViewOnly() {
      return this.$pagePermission && this.$pagePermission.isViewOnly('ToolManagement');
    },
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('ToolManagement'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('ToolManagement'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('ToolManagement'));
    },
    permissions() {
      return {
        add: this.$pagePermission && this.$pagePermission.canAdd('ToolManagement'),
        edit: this.$pagePermission && this.$pagePermission.canEdit('ToolManagement'),
        delete: this.$pagePermission && this.$pagePermission.canDelete('ToolManagement'),
      }
    },
  },
  data() {
    const todayDate = new Date();
    const dd = String(todayDate.getDate()).padStart(2, '0');
    const mm = String(todayDate.getMonth() + 1).padStart(2, '0');
    const yyyy = todayDate.getFullYear();

    return {
      today: `${dd}/${mm}/${yyyy}`,
      // Table
      headers: [
        { title: 'Tool', key: 'name', sortable: true },
        { title: 'Method', key: 'httpMethod', sortable: true, width: '110px' },
        { title: 'API Endpoint', key: 'apiEndpoint', sortable: true },
        { title: 'Updated', key: 'updateDate', sortable: true, width: '140px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '100px', align: 'center' },
      ],
      items: [],
      totalItems: 0,
      loading: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      searchText: '',
      searchTimeout: null,

      // Dialog
      dialog: false,
      isEdit: false,
      formValid: false,
      saving: false,
      formData: {
        id: null,
        name: '',
        description: '',
        apiEndpoint: '',
        httpMethod: 'POST',
        headers: [],
        parameters: [],
      },

      // Delete Dialog
      deleteDialog: false,
      deleting: false,
      selectedItem: null,

      // Test API
      testExpanded: false,
      testing: false,
      testParams: {},
      testResult: null,

      // Snackbar
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',

      // Options
      httpMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
      paramTypes: ['string', 'number', 'boolean', 'object', 'array'],

      // Validation Rules
      rules: {
        required: (v) => !!v || 'Required',
        url: (v) => {
          if (!v) return true;
          const pattern = /^https?:\/\/.+/i;
          return pattern.test(v) || 'URL must start with http:// or https://';
        },
      },
    };
  },
  watch: {
    options: {
      handler() {
        this.loadItems();
      },
      deep: true,
      immediate: false,
    },
  },
  mounted() {
    this.loadItems();
  },
  methods: {
    loadItems() {
      this.loading = true;
      const { page, itemsPerPage, sortBy } = this.options;

      // Use PascalCase to match C# model (AgentToolSearchRequest)
      const request = {
        SearchText: this.searchText || '',
        Page: page || 1,
        ItemsPerPage: itemsPerPage || 10,
        SortBy: sortBy?.[0]?.key || '',
        SortDesc: sortBy?.[0]?.order === 'desc',
      };

      this.$call.SearchAgentTools(
        request,
        (res) => {
          console.log(res);
          // Check for successful response (status can be 'successful' string or 0 number)
          if ((res.status === 'successful' || res.status === 0) && res.data) {
            // Data is already camelCase from API
            this.items = res.data.items || res.data.Items || [];
            this.totalItems = res.data.totalCount ?? res.data.TotalCount ?? 0;
          } else {
            this.items = [];
            this.totalItems = 0;
          }
          this.loading = false;
        },
        () => {
          this.showSnackbar('Failed to load data', 'error');
          this.loading = false;
        }
      );
    },

    onSearchDebounced() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1;
        this.loadItems();
      }, 400);
    },

    openAddDialog() {
      this.isEdit = false;
      this.formData = {
        id: null,
        name: '',
        description: '',
        apiEndpoint: '',
        httpMethod: 'POST',
        headers: [],
        parameters: [],
      };
      this.resetTestState();
      this.dialog = true;
      this.$nextTick(() => {
        this.$refs.formRef?.resetValidation();
      });
    },

    openEditDialog(item) {
      this.isEdit = true;
      // Handle both PascalCase and camelCase properties
      const headers = item.Headers ?? item.headers ?? [];
      const parameters = item.Parameters ?? item.parameters ?? [];
      this.formData = {
        id: item.ID ?? item.id,
        name: item.Name ?? item.name,
        description: item.Description ?? item.description ?? '',
        apiEndpoint: item.ApiEndpoint ?? item.apiEndpoint,
        httpMethod: item.HttpMethod ?? item.httpMethod ?? 'POST',
        headers: JSON.parse(JSON.stringify(headers)),
        parameters: JSON.parse(JSON.stringify(parameters)),
      };
      this.resetTestState();
      this.dialog = true;
      this.$nextTick(() => {
        this.$refs.formRef?.resetValidation();
      });
    },

    closeDialog() {
      this.dialog = false;
      this.$refs.formRef?.reset();
    },

    addHeader() {
      this.formData.headers.push({ key: '', value: '' });
    },

    removeHeader(index) {
      this.formData.headers.splice(index, 1);
    },

    addParameter() {
      this.formData.parameters.push({
        name: '',
        type: 'string',
        description: '',
        required: false,
      });
    },

    removeParameter(index) {
      this.formData.parameters.splice(index, 1);
    },

    async saveData() {
      const validation = await this.$refs.formRef.validate();
      if (!validation.valid) return;

      this.saving = true;

      // Use PascalCase to match C# model (AgentToolRequestModel)
      const request = {
        ID: this.formData.id,
        Name: this.formData.name?.trim(),
        Description: this.formData.description?.trim(),
        ApiEndpoint: this.formData.apiEndpoint?.trim(),
        HttpMethod: this.formData.httpMethod,
        Headers: this.formData.headers.filter((h) => h.key).map(h => ({ Key: h.key, Value: h.value })),
        Parameters: this.formData.parameters.filter((p) => p.name).map(p => ({
          Name: p.name,
          Type: p.type,
          Description: p.description,
          Required: p.required,
        })),
      };

      const apiCall = this.isEdit ? this.$call.UpdateAgentTool : this.$call.CreateAgentTool;

      apiCall.call(this.$call, request, (res) => {
        console.log(res);
        if (res.status === 'successful' || res.status === 0) {
          this.showSnackbar(this.isEdit ? 'Updated successfully' : 'Created successfully', 'success');
          this.closeDialog();
          this.loadItems();
        } else {
          this.showSnackbar(res.message || 'An error occurred', 'error');
        }
        this.saving = false;
      }, () => {
        this.showSnackbar('Failed to save data', 'error');
        this.saving = false;
      });
    },

    openDeleteDialog(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },

    confirmDelete() {
      if (!this.selectedItem) return;

      this.deleting = true;

      this.$call.DeleteAgentTool(this.selectedItem.id, (res) => {
        if (res.status === 'successful' || res.status === 0) {
          this.showSnackbar('Deleted successfully', 'success');
          this.deleteDialog = false;
          this.loadItems();
        } else {
          this.showSnackbar(res.message || 'An error occurred', 'error');
        }
        this.deleting = false;
      }, () => {
        this.showSnackbar('Failed to delete data', 'error');
        this.deleting = false;
      });
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    formatDate(date) {
      if (!date) return '-';
      const parsed = new Date(date);
      if (isNaN(parsed.getTime())) return '-';

      const dd = String(parsed.getDate()).padStart(2, '0');
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      const yyyy = parsed.getFullYear();
      const hh = String(parsed.getHours()).padStart(2, '0');
      const min = String(parsed.getMinutes()).padStart(2, '0');

      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    },

    getMethodColor(method) {
      const colors = {
        GET: 'blue',
        POST: 'green',
        PUT: 'orange',
        DELETE: 'red',
        PATCH: 'purple',
      };
      return colors[method] || 'grey';
    },

    getTypeColor(type) {
      const colors = {
        string: 'blue',
        number: 'orange',
        boolean: 'purple',
        object: 'teal',
        array: 'pink',
      };
      return colors[type] || 'grey';
    },

    getTypeIcon(type) {
      const icons = {
        string: 'mdi-format-text',
        number: 'mdi-numeric',
        boolean: 'mdi-toggle-switch',
        object: 'mdi-code-json',
        array: 'mdi-code-array',
      };
      return icons[type] || 'mdi-help';
    },

    // Test API Methods
    getTestPlaceholder(param) {
      const placeholders = {
        string: 'Enter text...',
        number: '123',
        boolean: 'true or false',
        object: '{"key": "value"}',
        array: '["item1", "item2"]',
      };
      return placeholders[param.type] || 'Enter value...';
    },

    getStatusColor(status) {
      if (!status) return 'grey';
      if (status >= 200 && status < 300) return 'success';
      if (status >= 300 && status < 400) return 'info';
      if (status >= 400 && status < 500) return 'warning';
      return 'error';
    },

    formatHeaders(headers) {
      if (!headers) return 'No headers';
      if (typeof headers === 'string') return headers;
      try {
        return Object.entries(headers)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n');
      } catch {
        return JSON.stringify(headers, null, 2);
      }
    },

    formatResponse(data) {
      if (data === null || data === undefined) return 'No response data';
      if (typeof data === 'string') {
        try {
          return JSON.stringify(JSON.parse(data), null, 2);
        } catch {
          return data;
        }
      }
      return JSON.stringify(data, null, 2);
    },

    async runTest() {
      this.testing = true;
      this.testResult = null;

      const startTime = Date.now();

      try {
        // Build headers object
        const headersObj = {};
        this.formData.headers.forEach(h => {
          if (h.key) {
            headersObj[h.key] = h.value;
          }
        });

        // Build request body/params based on parameters
        const requestData = {};
        this.formData.parameters.forEach(param => {
          let value = this.testParams[param.name];
          if (value !== undefined && value !== '') {
            // Convert value based on type
            if (param.type === 'number') {
              value = Number(value);
            } else if (param.type === 'boolean') {
              value = value === 'true' || value === true;
            } else if (param.type === 'object' || param.type === 'array') {
              try {
                value = JSON.parse(value);
              } catch (e) {
                // Keep as string if parse fails
              }
            }
            requestData[param.name] = value;
          }
        });

        // Prepare fetch options
        const fetchOptions = {
          method: this.formData.httpMethod,
          headers: {
            'Content-Type': 'application/json',
            ...headersObj
          }
        };

        // Add body for non-GET requests
        let url = this.formData.apiEndpoint;
        if (this.formData.httpMethod === 'GET') {
          // Add params as query string for GET
          const queryParams = new URLSearchParams();
          Object.entries(requestData).forEach(([key, value]) => {
            queryParams.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
          });
          const queryString = queryParams.toString();
          if (queryString) {
            url += (url.includes('?') ? '&' : '?') + queryString;
          }
        } else {
          fetchOptions.body = JSON.stringify(requestData);
        }

        const response = await fetch(url, fetchOptions);
        const duration = Date.now() - startTime;

        // Get response headers
        const responseHeaders = {};
        response.headers.forEach((value, key) => {
          responseHeaders[key] = value;
        });

        // Try to parse response
        let responseData;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
        } else {
          responseData = await response.text();
        }

        this.testResult = {
          success: response.ok,
          status: response.status,
          statusText: response.statusText,
          headers: responseHeaders,
          data: responseData,
          duration
        };

      } catch (error) {
        const duration = Date.now() - startTime;
        this.testResult = {
          success: false,
          status: null,
          statusText: 'Network Error',
          headers: null,
          data: error.message || 'Failed to connect to API',
          duration
        };
      }

      this.testing = false;
    },

    resetTestState() {
      this.testExpanded = false;
      this.testParams = {};
      this.testResult = null;
    },
  },
};
</script>

<style scoped>
.main-card {
  border-radius: 16px !important;
  margin-bottom: 2rem;
}

.header-title {
  border-radius: 12px;
}

.search-field :deep(.v-field) {
  background: #f5f5f5 !important;
  border-radius: 8px;
}

.add-btn {
  border-radius: 8px;
  font-weight: 500;
}

.table-container {
  overflow-x: auto;
  max-width: 100%;
}

:deep(.v-data-table) {
  border: 1px solid #e1e1e1;
  border-radius: 12px;
  overflow: hidden;
}

:deep(.v-table__wrapper thead tr),
:deep(.v-table__wrapper thead th) {
  background-color: #fafafa !important;
  font-weight: 600 !important;
}

.action-group {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.action-btn {
  border-radius: 8px;
}

.api-endpoint {
  font-size: 12px;
  color: #1a73e8;
  background: #e3f2fd;
  padding: 4px 8px;
  border-radius: 4px;
  word-break: break-all;
}

/* Dialog Styles */
.dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #424242;
}

.header-row,
.param-row {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.empty-section {
  background: #fafafa;
  border: 1px dashed #e0e0e0;
}

/* Parameter Card Styles */
.param-card {
  border-radius: 12px !important;
  border-color: #e0e0e0 !important;
  transition: all 0.2s ease;
}

.param-card:hover {
  border-color: #26a69a !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.param-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.params-container {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 4px;
}

.params-container::-webkit-scrollbar {
  width: 6px;
}

.params-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.params-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.params-container::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Test API Styles */
.test-section {
  margin-bottom: 8px;
}

.test-card {
  border-radius: 12px !important;
  border-color: #e0e0e0 !important;
  background: linear-gradient(135deg, #fafafa 0%, #ffffff 100%);
}

.test-param-input :deep(.v-field) {
  background: white !important;
}

.test-result {
  border-top: 1px solid #e0e0e0;
  padding-top: 12px;
}

.response-headers {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 11px;
  background: #f5f5f5;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 120px;
  overflow-y: auto;
  color: #616161;
}

.response-body {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  background: #263238;
  color: #aed581;
  padding: 12px 16px;
  border-radius: 8px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
  line-height: 1.5;
}

.response-body.error-response {
  background: #ffebee;
  color: #c62828;
}

.response-body::-webkit-scrollbar,
.response-headers::-webkit-scrollbar {
  width: 6px;
}

.response-body::-webkit-scrollbar-track,
.response-headers::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.response-body::-webkit-scrollbar-thumb,
.response-headers::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.response-headers::-webkit-scrollbar-thumb {
  background: #c1c1c1;
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper thead th) {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}
</style>
